"use client"

import { useState, useRef, Suspense } from "react"
import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"
import { useToolFromQuery, parseGrassType, parseZipCode } from "@/hooks/useToolFromQuery"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { AerationForm } from "@/components/tools/aeration-form"
import { HeadlineHero, LawnPrefillBanner, ToolDataChartSkeleton, RelatedTools, ResultSuccessCard, ResultEmailCaptureCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import type { AerationResult } from "@/lib/aeration-calculator"

import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { useFromTool } from "@/hooks/use-from-tool"

const AerationDataChart = dynamic(
  () => import("@/components/tools/charts/aeration-data-chart").then((m) => m.AerationDataChart),
  { ssr: false, loading: () => <ToolDataChartSkeleton height={280} /> }
)

function AerationCalculatorContent() {
  const searchParams = useSearchParams()
  useFromTool("aeration-timing-tool")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } =
    useNewsletterPrompt()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const [initialZip, setInitialZip] = useState<string | undefined>(undefined)
  const [initialGrass, setInitialGrass] = useState<string | undefined>(undefined)
  const [liveResult, setLiveResult] = useState<AerationResult | null>(null)
  const trackedUsageRef = useRef(false)

  // URL prefill for shareable links — the form is fully live, so prefilling
  // the inputs is the only step needed.
  useToolFromQuery({
    parsers: { zip: parseZipCode, grass: parseGrassType },
    required: ["zip", "grass"] as const,
    onAutoSubmit: ({ zip, grass }) => {
      setInitialZip(zip)
      setInitialGrass(grass)
    },
  })

  const handleResultChange = (result: AerationResult | null) => {
    setLiveResult(result)
    if (result && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage("aeration-calculator")
      analytics.calculatorUsed("aeration", {
        inputs: {
          grass_type: result.grassType,
          zip_code: result.zipCode,
          region: result.region,
        },
        result: {
          recommended_season: result.recommendedSeason,
          status: result.status,
          is_optimal_time: result.isOptimalTime,
          current_season: result.currentSeason,
        },
        source: "live_update",
      })
    }
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Aeration Calculator" },
          ]}
        />

        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Timed to your grass"
              title="When should you"
              accent="aerate?"
              subtitle="Free aeration calculator by ZIP and grass type. Pick traffic level and last-aerated date. The 12-month calendar fills in as you go."
            />
          </div>

          {/* Form with live preview — the only results UI on this page */}
          <div className="mb-8">
            <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
            <AerationForm
              initialZip={initialZip ?? lawnPrefill.zipCode}
              initialGrass={initialGrass ?? lawnPrefill.grassType}
              initialLawnSize={lawnPrefill.lawnSizeSqft}
              initialSunlight={searchParams.get("sunlight") ?? lawnPrefill.sunlight}
              onResultChange={handleResultChange}
            />
          </div>

          {liveResult && (
            <div className="mt-8 space-y-6">
              <AerationSuccessCard result={liveResult} />
              <ResultEmailCaptureCard
                source="tool_result_aeration"
                toolDisplayName="aeration plan"
                subcopy="Seasonal reminders for when aeration conditions line up in your area."
                grassType={liveResult.grassType}
                zipCode={liveResult.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("aeration-timing-tool", {
                    grassType: liveResult.grassType,
                    zip: liveResult.zipCode,
                  }),
                  headline: `Save my aeration plan for ${liveResult.grassType}`,
                  subcopy: "Keep this aeration plan in a free account and track your lawn over time.",
                  dividerLabel: "or get your 12-month calendar by email",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_aeration"),
                }}
                onCaptured={() => {
                  closeNewsletterModal()
                }}
              />
              <AerationDataChart result={liveResult} />
              <ProductRecommendations
                categories={["Lawn Mowers & Maintenance Tools"]}
                title="Aeration tools to have ready"
                description="Core aerators and the gear to do it right."
                trackingContext="aeration-calculator"
                keywords={['aeration', 'core aerator', 'plug aerator', liveResult.grassType].filter(Boolean)}
                compact
              />
            </div>
          )}

          <RelatedTools
            currentToolId="aeration-timing-tool"
            prefillValues={{
              zip: liveResult?.zipCode ?? initialZip ?? lawnPrefill.zipCode,
              grassType: liveResult?.grassType ?? initialGrass ?? lawnPrefill.grassType,
              lawnSize: lawnPrefill.lawnSizeSqft,
              sunlight: searchParams.get("sunlight") ?? lawnPrefill.sunlight,
            }}
          />
        </div>
      </Reveal>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Aeration Timing Calculator"
        usageCount={usageCount}
        grassType={liveResult?.grassType}
        zipCode={liveResult?.zipCode}
      />
    </div>
  )
}

const AERATION_SEASON_LABELS: Record<string, string> = {
  spring: "this spring",
  summer: "this summer",
  fall: "this fall",
  winter: "this winter",
  now: "right now",
  wait: "later this season",
}

/**
 * Bounded peak-moment frame for the aeration result: timing recommendation
 * (hero-less — no single clean number) + one-sentence interpretation naming
 * the user's grass and current status + one primary next-action (check soil
 * temperature before aerating).
 */
function AerationSuccessCard({ result }: { result: AerationResult }) {
  const seasonLabel = AERATION_SEASON_LABELS[result.recommendedSeason] ?? result.recommendedSeason
  const isGoodTime = result.status === "acceptable" || result.status === "optimal" || result.status === "approaching"

  const soilTempHref = buildRelatedToolUrl("soil-temperature", "aeration-timing-tool", {
    zip: result.zipCode,
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "aeration-timing-tool",
      targetTool: "soil-temperature",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Confirm soil temperature before aerating",
      prefillFields: result.zipCode ? "zip" : "",
    })
  }

  const eyebrow = isGoodTime ? "Good time to aerate" : "Not quite time yet"

  return (
    <ResultSuccessCard
      eyebrow={eyebrow}
      interpretation={
        <>
          Best time to aerate your {result.grassType} lawn is {seasonLabel}. {result.timingAdvice}
        </>
      }
      cta={{
        href: soilTempHref,
        label: "Check soil temperature",
        onClick: handleCtaClick,
      }}
      share={{
        url: buildToolUrl("aeration-timing-tool", {
          grassType: result.grassType,
          zip: result.zipCode,
        }),
        title: "Aeration Timing",
        text: `Aeration timing for my ${result.grassType} lawn`,
        trackingId: "aeration-timing-tool",
      }}
    />
  )
}

export function AerationCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="aeration-timing-tool" />}>
      <AerationCalculatorContent />
    </Suspense>
  )
}
